import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getCsrfToken } from "next-auth/react";
import { GetServerSidePropsContext } from "next";
import Head from "next/head";
import Script from "next/script";
import { PageTitle } from "@/lib/mui/PageTitle";
import { Paragraph } from "@/lib/mui/Paragraph";
import { simpleArg } from "@/lib/next/simpleArg";
import { strictEnv } from "@/lib/utils/env";
import { testMode } from "@/modules/authentication/queries";
import { getWebsiteLayout } from "@/modules/navigation/website-layout/WebsiteLayout";
import { PageLayout } from "@/modules/navigation/website-layout/PageLayout";
import { appConfig } from "../config/app-config";
import { NextPageWithLayout } from "./_app";

type PageProps = {
  csrfToken: string;
  callbackUrl: string | null;
  turnstileSiteKey: string | null;
};

const Page: NextPageWithLayout<PageProps> = ({
  csrfToken,
  callbackUrl,
  turnstileSiteKey,
}) => {
  return (
    <>
      <Head>
        <title>{`Registration / Login – ${appConfig.name}`}</title>
      </Head>
      {turnstileSiteKey && (
        <>
          <Script id="turnstile-error-handler" strategy="afterInteractive">
            {`window.onTurnstileError = function () { return true; };`}
          </Script>
          <Script
            src="https://challenges.cloudflare.com/turnstile/v0/api.js"
            strategy="afterInteractive"
          />
        </>
      )}

      <PageLayout>
        <form method="post" action="/api/auth/signin/email">
          <input name="csrfToken" type="hidden" defaultValue={csrfToken} />
          {callbackUrl && (
            <input name="callbackUrl" type="hidden" value={callbackUrl} />
          )}

          <PageTitle>Registration / Login</PageTitle>

          <Paragraph>
            Enter your email and you we will send you a link to sign in with.
          </Paragraph>

          <Paragraph>
            If you do not have an account yet, one will be created for you.
          </Paragraph>

          <Box mb={2}>
            <TextField
              fullWidth
              type="email"
              autoFocus
              id="email"
              name="email"
              label="Email"
              variant="outlined"
            />
          </Box>

          {turnstileSiteKey && (
            <Box mb={2}>
              <div
                className="cf-turnstile"
                data-sitekey={turnstileSiteKey}
                data-action="login"
                data-error-callback="onTurnstileError"
              />
            </Box>
          )}

          <Button type="submit" variant="contained">
            Log in with Email
          </Button>
        </form>
      </PageLayout>
    </>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const csrfToken = await getCsrfToken(context);
  if (!csrfToken) {
    throw new Error("CSRF token not found");
  }

  const callbackUrl = simpleArg(context.query.callbackUrl);
  const turnstileSiteKey = testMode()
    ? null
    : strictEnv("CLOUDFLARE_TURNSTILE_SITE_KEY");

  return {
    props: { csrfToken, callbackUrl, turnstileSiteKey } satisfies PageProps,
  };
}

Page.getLayout = getWebsiteLayout;

export default Page;
